import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ExternalLink, Search, ArrowUp, ArrowDown, ArrowUpDown, Sparkles, Rocket, Download, ClipboardList, Flame } from "lucide-react";
import { AppDialog } from "./tracker/AppDialog";
import { DashboardCards } from "./tracker/DashboardCards";
import { STATUSES, STATUS_STYLES, statusLabel } from "./tracker/constants";
import { ApplyAssistPanel } from "@/components/ApplyAssistPanel";
import { InterviewPrepDialog } from "@/components/InterviewPrepDialog";
import { NhsSupportingDialog } from "@/components/NhsSupportingDialog";

export default function Tracker() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [tailoredCvs, setTailoredCvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [panelJob, setPanelJob] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isPro, setIsPro] = useState(false);

  const load = async () => {
    const [a, t, s] = await Promise.all([
      api.get("/applications"),
      api.get("/tailored-cvs"),
      api.get("/billing/subscription").catch(() => ({ data: {} })),
    ]);
    setApps(a.data);
    setTailoredCvs(t.data);
    setIsPro(s.data?.status === "active" && ["pro", "sponsored"].includes(s.data?.tier));
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const del = async (id) => {
    await api.delete(`/applications/${id}`);
    toast.success("Deleted");
    load();
  };

  const cvLabel = (id) => {
    const t = tailoredCvs.find((x) => x.id === id);
    return t ? `${t.job_title} · ${t.company}` : "—";
  };

  const tailorFrom = (a) => {
    navigate("/tailor", { state: { prefill: { job_title: a.job_title, company: a.company, job_description: a.job_description || "", job_url: a.job_url || "" } } });
  };

  const openApply = (a) => { setPanelJob(a); setPanelOpen(true); };

  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const weekCount = apps.filter((a) => {
    const t = new Date(a.created_at).getTime();
    return !isNaN(t) && t >= weekAgo;
  }).length;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/applications/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "jobsmith_applications.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Couldn't export CSV.");
    } finally { setExporting(false); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const q = search.trim().toLowerCase();
  const visible = apps
    .filter((a) => statusFilter === "all" || a.status === statusFilter)
    .filter((a) => !q || `${a.company} ${a.job_title} ${a.notes || ""}`.toLowerCase().includes(q))
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const va = (a[sortKey] || "").toString().toLowerCase();
      const vb = (b[sortKey] || "").toString().toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });

  const sortIcon = (colKey) => {
    if (sortKey !== colKey) return <ArrowUpDown className="h-3 w-3 opacity-40" strokeWidth={2} />;
    if (sortDir === "asc") return <ArrowUp className="h-3 w-3" strokeWidth={2} />;
    return <ArrowDown className="h-3 w-3" strokeWidth={2} />;
  };

  const SortHead = ({ label, colKey, className = "" }) => (
    <TableHead className={`text-xs uppercase tracking-wider ${className}`}>
      <button onClick={() => toggleSort(colKey)} className="inline-flex items-center gap-1 hover:text-foreground transition-colors" data-testid={`sort-${colKey}`}>
        {label}
        {sortIcon(colKey)}
      </button>
    </TableHead>
  );

  return (
    <div className="space-y-6" data-testid="tracker-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Application Tracker</h1>
          <p className="text-muted-foreground mt-1 text-sm">Track every job you apply to.</p>
          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8A93C]/20 to-[#24468C]/10 border border-[#E8A93C]/30 px-3 py-1" data-testid="tracker-week-streak">
            <Flame className={`h-4 w-4 ${weekCount > 0 ? "text-[#E8A93C]" : "text-muted-foreground"}`} strokeWidth={2} />
            <span className="text-sm font-bold text-[#8a6410]" data-testid="tracker-week-count">{weekCount}</span>
            <span className="text-xs text-muted-foreground">application{weekCount === 1 ? "" : "s"} this week{weekCount > 0 ? " — keep it up!" : ""}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={exporting || apps.length === 0} data-testid="tracker-export-csv-button" className="active:scale-[0.98] transition-transform">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Export CSV
          </Button>
          <Button onClick={() => navigate("/apply")} data-testid="tracker-quick-apply-button" className="active:scale-[0.98] transition-transform">
            <Rocket className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Quick Apply
          </Button>
          <AppDialog tailoredCvs={tailoredCvs} onSaved={load} />
        </div>
      </div>

      {!loading && <DashboardCards apps={apps} />}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : apps.length === 0 ? (
        <Card className="border-dashed border-zinc-300 shadow-none flex flex-col items-center justify-center py-16 text-center">
          <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mb-3">
            <Rocket className="h-6 w-6 text-accent" strokeWidth={1.5} />
          </div>
          <p className="font-medium">No applications yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">Fetch a job, tailor your CV, and track it — all in one guided flow.</p>
          <Button onClick={() => navigate("/apply")} className="mt-4 active:scale-[0.98] transition-transform" data-testid="tracker-empty-quick-apply-button">
            <Rocket className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Start a Quick Apply
          </Button>
        </Card>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search company, role, notes…" className="pl-9" data-testid="tracker-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="tracker-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground sm:ml-auto" data-testid="tracker-result-count">{visible.length} of {apps.length}</span>
          </div>

          <Card className="border-zinc-200 shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHead label="Company" colKey="company" />
                  <SortHead label="Role" colKey="job_title" />
                  <SortHead label="Date" colKey="date_applied" />
                  <SortHead label="Status" colKey="status" />
                  <TableHead className="text-xs uppercase tracking-wider">Tailored CV</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Notes</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-10" data-testid="tracker-no-results">No applications match your filters.</TableCell>
                  </TableRow>
                ) : visible.map((a) => (
                  <TableRow key={a.id} className="hover:bg-zinc-50" data-testid={`app-row-${a.id}`}>
                    <TableCell className="font-medium">{a.company}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {a.job_title}
                        {a.job_url && <a href={a.job_url} target="_blank" rel="noreferrer" className="text-accent"><ExternalLink className="h-3.5 w-3.5" /></a>}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{a.date_applied || "—"}</TableCell>
                    <TableCell><Badge className={`${STATUS_STYLES[a.status]} hover:${STATUS_STYLES[a.status]}`}>{statusLabel(a.status)}</Badge></TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[160px] truncate">{a.tailored_cv_id ? cvLabel(a.tailored_cv_id) : "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{a.notes || "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-[#24468C]" onClick={() => openApply(a)} data-testid={`apply-assist-${a.id}`} title="Apply-Assist helper">
                          <ClipboardList className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-accent" onClick={() => tailorFrom(a)} data-testid={`tailor-from-app-${a.id}`} title="Tailor CV from this application">
                          <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <InterviewPrepDialog app={a} isPro={isPro} />
                        <NhsSupportingDialog app={a} isPro={isPro} />
                        <AppDialog app={a} tailoredCvs={tailoredCvs} onSaved={load} />
                        <ConfirmDelete
                          onConfirm={() => del(a.id)}
                          triggerTestId={`delete-app-${a.id}`}
                          confirmTestId={`confirm-delete-app-${a.id}`}
                          title="Delete this application?"
                          description={`"${a.job_title} · ${a.company}" will be permanently removed.`}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      <ApplyAssistPanel open={panelOpen} onOpenChange={setPanelOpen} job={panelJob} onApplied={load} />
    </div>
  );
}
